import { useMemo, useState } from 'react'
import {
  Printer, Copy, Check, ChevronDown,
  CheckCircle2, Clock, Circle, AlertTriangle, Users2,
} from 'lucide-react'
import { useApp } from '@/context/AppContext'
import { effectiveTaskStatus, picColor, parsePics } from '@/lib/utils'
import type { Task, TaskStatus } from '@/lib/types'

type Period = 'week' | 'month' | 'all'

const PERIODS: { key: Period; label: string }[] = [
  { key: 'week', label: 'Mingguan' },
  { key: 'month', label: 'Bulanan' },
  { key: 'all', label: 'Keseluruhan' },
]

const STATUS_LABEL: Record<TaskStatus, string> = {
  'done': 'Selesai',
  'in-progress': 'Sedang Dikerjakan',
  'not-started': 'Belum Mulai',
}

type TaskRow = { task: Task; status: TaskStatus; done: number; total: number; overdue: boolean }
type GroupedProject = { name: string; color: string; tasks: TaskRow[] }
type Stats = { done: number; active: number; todo: number; overdue: number; total: number }
type MemberData = { name: string; grouped: GroupedProject[]; stats: Stats; subtaskTotal: number; weight: number }

// ─── Minimal markdown renderer ──────────────────────────────────────────────
function Markdown({ text }: { text: string }) {
  const lines = text.split('\n')
  const out: React.ReactNode[] = []
  let bullets: string[] = []
  const inline = (s: string) => s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  const flushBullets = (key: string) => {
    if (bullets.length === 0) return
    out.push(
      <ul key={key} className="list-disc pl-5 space-y-1 my-2">
        {bullets.map((b, i) => <li key={i} className="text-[13px] text-[#3A3A36] font-body leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(b) }} />)}
      </ul>
    )
    bullets = []
  }
  lines.forEach((line, i) => {
    const t = line.trim()
    if (t.startsWith('## ')) { flushBullets(`ul-${i}`); out.push(<h3 key={i} className="font-display text-[15px] font-bold text-[#1A1916] mt-4 mb-1.5">{t.slice(3)}</h3>) }
    else if (t.startsWith('- ') || t.startsWith('* ')) bullets.push(t.slice(2))
    else if (t === '') flushBullets(`ul-${i}`)
    else { flushBullets(`ul-${i}`); out.push(<p key={i} className="text-[13px] text-[#3A3A36] font-body leading-relaxed my-1.5" dangerouslySetInnerHTML={{ __html: inline(t) }} />) }
  })
  flushBullets('ul-end')
  return <>{out}</>
}

// ─── Avatar ──────────────────────────────────────────────────────────────────
function Avatar({ name, size = 'sm' }: { name: string; size?: 'sm' | 'lg' }) {
  const cls = size === 'lg' ? 'w-11 h-11 text-base' : 'w-6 h-6 text-[10px]'
  return (
    <div className={`${cls} rounded-full flex items-center justify-center font-bold text-white flex-shrink-0`}
      style={{ backgroundColor: picColor(name) }} title={name}>
      {name.charAt(0).toUpperCase()}
    </div>
  )
}

// ─── 4 kartu statistik ─────────────────────────────────────────────────────────
function StatGrid({ s }: { s: Stats }) {
  return (
    <div className="grid grid-cols-4 gap-2 py-4">
      {[
        { label: 'Selesai', val: s.done, color: '#16A34A', Icon: CheckCircle2 },
        { label: 'Berjalan', val: s.active, color: '#F59E0B', Icon: Clock },
        { label: 'Belum Mulai', val: s.todo, color: '#94A3B8', Icon: Circle },
        { label: 'Telat', val: s.overdue, color: '#DC2626', Icon: AlertTriangle },
      ].map(({ label, val, color, Icon }) => (
        <div key={label} className="border border-[#F0EFE9] rounded-xl px-3 py-2.5 text-center">
          <Icon className="w-3.5 h-3.5 mx-auto mb-1" style={{ color }} />
          <p className="font-display text-xl font-bold leading-none" style={{ color }}>{val}</p>
          <p className="text-[9px] uppercase tracking-wider text-[#C0BDB6] font-body mt-1">{label}</p>
        </div>
      ))}
    </div>
  )
}

// ─── Rincian task per project ──────────────────────────────────────────────────
function TaskBreakdown({ grouped }: { grouped: GroupedProject[] }) {
  if (grouped.length === 0) return <p className="text-[13px] text-[#AEADA8] font-body">Tidak ada task untuk anggota ini.</p>
  return (
    <div className="space-y-4">
      {grouped.map((g) => (
        <div key={g.name}>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: g.color }} />
            <p className="text-[13px] font-body font-semibold text-[#1A1916]">{g.name}</p>
          </div>
          <div className="space-y-1 pl-4">
            {g.tasks.map((x) => (
              <div key={x.task.id} className="flex items-center gap-2 text-[12px] font-body">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                  backgroundColor: x.status === 'done' ? '#16A34A' : x.status === 'in-progress' ? '#F59E0B' : '#CBD5E1',
                }} />
                <span className={`flex-1 ${x.status === 'done' ? 'text-[#AEADA8] line-through' : 'text-[#3A3A36]'}`}>
                  {x.task.title}
                  {x.total > 0 && <span className="text-[#C0BDB6]"> · {x.done}/{x.total}</span>}
                </span>
                {x.overdue && (
                  <span className="flex items-center gap-1 text-[10px] text-[#DC2626] font-medium flex-shrink-0">
                    <AlertTriangle className="w-3 h-3" /> telat
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export function WorkReport() {
  const { projects, tasks, getTaskSubtasks } = useApp()
  const [selected, setSelected] = useState<string[]>([]) // [] = Semua Anggota
  const [period, setPeriod] = useState<Period>('week')
  // Catatan laporan ditulis sendiri oleh yang membuat laporan. Dulu ini diisi model,
  // dan itu masalahnya: narasinya ikut tercetak di PDF dan tersalin ke markdown sebagai
  // kalimat manusia, padahal isinya tebakan atas angka yang sudah ada di halaman.
  const [narrative, setNarrative] = useState('')
  const [copied, setCopied] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const picByProject = useMemo(
    () => new Map(projects.map((p) => [p.id, p.pic?.trim().toLowerCase() ?? ''])),
    [projects]
  )

  const people = useMemo(() => {
    const map = new Map<string, string>()
    for (const p of projects) if (p.pic?.trim()) map.set(p.pic.trim().toLowerCase(), p.pic.trim())
    for (const t of tasks) for (const n of parsePics(t.assignee)) map.set(n.toLowerCase(), n)
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b))
  }, [projects, tasks])

  // Daftar nama aktif: kalau tidak ada yang dipilih → semua anggota.
  // Hitung data per-anggota (grouped task + statistik + bobot).
  const members = useMemo<MemberData[]>(() => {
    const activeNames = selected.length > 0 ? selected.filter((n) => people.includes(n)) : people
    const today = new Date(); today.setHours(0, 0, 0, 0)
    return activeNames.map((name) => {
      const lower = name.toLowerCase()
      const mTasks = tasks.filter((t) => {
        // Task bisa dipegang beberapa orang; ia masuk laporan SETIAP PIC-nya.
        const pics = parsePics(t.assignee)
        if (pics.length) return pics.some((n) => n.toLowerCase() === lower)
        return picByProject.get(t.projectId) === lower
      })
      const byProject = new Map<string, GroupedProject>()
      let done = 0, active = 0, todo = 0, overdue = 0, subtaskTotal = 0
      for (const t of mTasks) {
        const proj = projects.find((p) => p.id === t.projectId)
        const subs = getTaskSubtasks(t.id)
        const status = effectiveTaskStatus(t.status, subs)
        subtaskTotal += subs.length
        const od = !!t.dueDate && status !== 'done' && new Date(t.dueDate) < today
        if (status === 'done') done++
        else if (status === 'in-progress') active++
        else todo++
        if (od) overdue++
        if (proj) {
          if (!byProject.has(proj.id)) byProject.set(proj.id, { name: proj.name, color: proj.color, tasks: [] })
          byProject.get(proj.id)!.tasks.push({ task: t, status, done: subs.filter((s) => s.completed).length, total: subs.length, overdue: od })
        }
      }
      return {
        name,
        grouped: Array.from(byProject.values()),
        stats: { done, active, todo, overdue, total: mTasks.length },
        subtaskTotal,
        weight: mTasks.length + subtaskTotal,
      }
    })
  }, [selected, people, tasks, projects, getTaskSubtasks, picByProject])

  const mode: 'individual' | 'comparison' = members.length === 1 ? 'individual' : 'comparison'
  const ranked = useMemo(() => [...members].sort((a, b) => b.weight - a.weight), [members])
  const maxWeight = Math.max(1, ...members.map((m) => m.weight))

  const teamStats = useMemo<Stats>(() =>
    members.reduce<Stats>((acc, m) => ({
      done: acc.done + m.stats.done,
      active: acc.active + m.stats.active,
      todo: acc.todo + m.stats.todo,
      overdue: acc.overdue + m.stats.overdue,
      total: acc.total + m.stats.total,
    }), { done: 0, active: 0, todo: 0, overdue: 0, total: 0 })
  , [members])

  const periodLabel = PERIODS.find((p) => p.key === period)!.label
  const dateStr = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })
  const canAct = members.length > 0
  const reportName = mode === 'individual'
    ? members[0].name
    : selected.length === 0 ? 'Tim JAL Group' : `${members.length} Anggota Terpilih`

  const selectLabel = selected.length === 0
    ? 'Semua Anggota'
    : selected.length <= 2 ? selected.join(', ') : `${selected.length} anggota dipilih`

  const toggle = (name: string) => {
    setSelected((prev) => prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name])
    setNarrative('')
  }

  const projectsPayload = (m: MemberData) =>
    m.grouped.map((g) => ({
      project: g.name,
      tasks: g.tasks.map((x) => ({
        title: x.task.title, description: x.task.description,
        status: STATUS_LABEL[x.status], priority: x.task.priority,
        dueDate: x.task.dueDate, subtask: x.total > 0 ? `${x.done}/${x.total}` : null, overdue: x.overdue,
      })),
    }))

  const buildMarkdown = () => {
    const lines: string[] = []
    if (mode === 'individual') {
      const m = members[0]
      lines.push(`# Laporan Kerja ${periodLabel} — ${m.name}`)
      lines.push(`_${dateStr}_`); lines.push('')
      lines.push(`**Ringkasan angka:** ${m.stats.done} selesai · ${m.stats.active} sedang dikerjakan · ${m.stats.todo} belum mulai${m.stats.overdue > 0 ? ` · ${m.stats.overdue} telat` : ''}`)
      lines.push('')
      if (narrative) { lines.push(narrative); lines.push('') }
      lines.push('## Rincian Task')
      for (const g of m.grouped) {
        lines.push(`\n### ${g.name}`)
        for (const x of g.tasks) {
          const prog = x.total > 0 ? ` (subtask ${x.done}/${x.total})` : ''
          const due = x.task.dueDate ? ` — deadline ${x.task.dueDate}${x.overdue ? ' ⚠️ TELAT' : ''}` : ''
          lines.push(`- [${STATUS_LABEL[x.status]}] ${x.task.title}${prog}${due}`)
        }
      }
      return lines.join('\n')
    }
    // Perbandingan
    lines.push(`# Laporan Perbandingan Tim ${periodLabel} — ${reportName}`)
    lines.push(`_${dateStr}_`); lines.push('')
    lines.push(`**Beban tim:** ${teamStats.done} selesai · ${teamStats.active} sedang dikerjakan · ${teamStats.todo} belum mulai${teamStats.overdue > 0 ? ` · ${teamStats.overdue} telat` : ''}`)
    lines.push('')
    lines.push('## Beban per Anggota (bobot = task + subtask)')
    for (const m of ranked) {
      lines.push(`- **${m.name}** — bobot ${m.weight} (${m.stats.total} task · ${m.subtaskTotal} subtask) · ${m.stats.active} aktif · ${m.stats.todo} todo · ${m.stats.done} done${m.stats.overdue > 0 ? ` · ${m.stats.overdue} telat` : ''}`)
    }
    lines.push('')
    if (narrative) { lines.push(narrative); lines.push('') }
    lines.push('## Rincian per Anggota')
    for (const m of ranked) {
      lines.push(`\n### ${m.name} — bobot ${m.weight}`)
      if (m.grouped.length === 0) { lines.push('- Belum ada task'); continue }
      for (const g of m.grouped) {
        lines.push(`\n#### ${g.name}`)
        for (const x of g.tasks) {
          const prog = x.total > 0 ? ` (subtask ${x.done}/${x.total})` : ''
          const due = x.task.dueDate ? ` — deadline ${x.task.dueDate}${x.overdue ? ' ⚠️ TELAT' : ''}` : ''
          lines.push(`- [${STATUS_LABEL[x.status]}] ${x.task.title}${prog}${due}`)
        }
      }
    }
    return lines.join('\n')
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(buildMarkdown())
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {/* Toolbar */}
      <div className="bg-white border border-[#E8E7E3] rounded-2xl p-4 space-y-3 no-print">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] uppercase tracking-widest text-[#C0BDB6] font-body mb-1.5">Anggota</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={menuOpen}
                className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 bg-[#F8F7F3] border border-[#E0DFD8] rounded-xl text-[#111110] text-sm font-body focus:outline-none focus:border-[#111110] focus:bg-white transition-all"
              >
                <span className="truncate text-left">{selectLabel}</span>
                <ChevronDown className={`w-4 h-4 text-[#AEADA8] flex-shrink-0 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                  <div role="listbox" className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 max-h-64 overflow-auto bg-white border border-[#E8E7E3] rounded-xl shadow-xl py-1.5 animate-fade-in">
                    <button
                      type="button" role="option" aria-selected={selected.length === 0}
                      onClick={() => { setSelected([]); setNarrative('') }}
                      className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm font-body transition-colors ${selected.length === 0 ? 'bg-[#F4F3F0] text-[#1A1916] font-semibold' : 'text-[#6B6860] hover:bg-[#F4F3F0]'}`}
                    >
                      <span className="w-5 h-5 rounded-md border border-[#C8C7BF] flex items-center justify-center flex-shrink-0 bg-white">
                        {selected.length === 0 && <Check className="w-3.5 h-3.5 text-[#6366F1]" />}
                      </span>
                      <Users2 className="w-4 h-4 text-[#8A8A85]" />
                      <span className="flex-1 text-left">Semua Anggota</span>
                    </button>
                    <div className="h-px bg-[#F0EFE9] my-1" />
                    {people.map((p) => {
                      const on = selected.includes(p)
                      return (
                        <button
                          key={p} type="button" role="option" aria-selected={on}
                          onClick={() => toggle(p)}
                          className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm font-body transition-colors ${on ? 'bg-[#F4F3F0] text-[#1A1916] font-medium' : 'text-[#6B6860] hover:bg-[#F4F3F0]'}`}
                        >
                          <span className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${on ? 'border-[#6366F1] bg-[#6366F1]' : 'border-[#C8C7BF] bg-white'}`}>
                            {on && <Check className="w-3.5 h-3.5 text-white" />}
                          </span>
                          <Avatar name={p} />
                          <span className="flex-1 text-left truncate">{p}</span>
                        </button>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
            <p className="text-[10px] text-[#C0BDB6] font-body mt-1">
              {mode === 'comparison' ? 'Mode perbandingan beban antar-anggota.' : 'Laporan individu.'}
            </p>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#C0BDB6] font-body mb-1.5">Periode</label>
            <div className="flex items-center gap-1 bg-[#F4F3F0] rounded-xl p-1">
              {PERIODS.map((p) => (
                <button key={p.key} onClick={() => { setPeriod(p.key); setNarrative('') }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-body font-medium transition-all ${period === p.key ? 'bg-white text-[#111110] shadow-sm' : 'text-[#8A8A85]'}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button onClick={() => window.print()} disabled={!canAct}
            className="flex items-center gap-2 px-4 py-2 bg-[#1A1916] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2A2A28] disabled:opacity-40 transition-all">
            <Printer className="w-3.5 h-3.5" /> Export PDF
          </button>
          <button onClick={handleCopy} disabled={!canAct}
            className="flex items-center gap-2 px-4 py-2 border border-[#E0DFD8] text-[#6B6860] rounded-xl text-sm font-medium font-body hover:border-[#C8C7BF] hover:text-[#1A1916] disabled:opacity-40 transition-all">
            {copied ? <><Check className="w-3.5 h-3.5 text-[#16A34A]" /> Tersalin</> : <><Copy className="w-3.5 h-3.5" /> Salin Markdown</>}
          </button>
        </div>
        {canAct && (
          <div className="pt-1 no-print">
            <label htmlFor="catatan-laporan" className="block text-[10px] uppercase tracking-widest text-[#C0BDB6] font-body mb-1.5">
              Catatan laporan <span className="normal-case tracking-normal text-[#C0BDB6]">opsional, ikut tercetak</span>
            </label>
            <textarea id="catatan-laporan" value={narrative} onChange={(e) => setNarrative(e.target.value)}
              rows={3}
              placeholder="Tulis kesimpulan atau catatanmu untuk laporan ini..."
              className="w-full px-3.5 py-2.5 bg-[#F8F7F3] border border-[#E0DFD8] rounded-xl text-[#111110] text-sm font-body placeholder:text-[#C8C7BF] focus:outline-none focus:border-[#111110] focus:bg-white resize-none leading-relaxed transition-all" />
          </div>
        )}
      </div>

      {/* Report document */}
      <div className="bg-white border border-[#E8E7E3] rounded-2xl p-7 print-area">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 pb-4 border-b border-[#F0EFE9]">
          <div className="flex items-center gap-3">
            {mode === 'individual' ? (
              <Avatar name={members[0].name} size="lg" />
            ) : (
              <div className="w-11 h-11 rounded-full bg-[#1A1916] flex items-center justify-center flex-shrink-0">
                <Users2 className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#C0BDB6] font-body">
                {mode === 'individual' ? `Laporan Kerja ${periodLabel}` : `Laporan Perbandingan Tim · ${periodLabel}`}
              </p>
              <h1 className="font-display text-2xl font-bold text-[#1A1916] leading-tight">{reportName}</h1>
              <p className="text-[11px] text-[#AEADA8] font-body mt-0.5">
                {dateStr} · JAL Group{mode === 'comparison' ? ` · ${members.length} anggota` : ''}
              </p>
            </div>
          </div>
        </div>

        {!canAct ? (
          <p className="py-8 text-[13px] text-[#AEADA8] font-body text-center">Belum ada anggota dengan task.</p>
        ) : mode === 'individual' ? (
          <>
            <StatGrid s={members[0].stats} />
            {narrative ? (
              <div className="py-2 border-t border-[#F0EFE9]"><Markdown text={narrative} /></div>
            ) : (
              <div className="py-3 border-t border-[#F0EFE9] no-print">
                <p className="text-[12px] text-[#AEADA8] font-body italic">
                  Catatan laporan belum diisi.
                </p>
              </div>
            )}
            <div className="pt-3 border-t border-[#F0EFE9]">
              <h3 className="font-display text-[15px] font-bold text-[#1A1916] mb-3">Rincian Task</h3>
              <TaskBreakdown grouped={members[0].grouped} />
            </div>
          </>
        ) : (
          <>
            <StatGrid s={teamStats} />

            {/* Sebaran beban per anggota */}
            <div className="pt-1 pb-3 border-t border-[#F0EFE9]">
              <h3 className="font-display text-[15px] font-bold text-[#1A1916] mb-3">
                Sebaran Beban <span className="text-[11px] font-body font-normal text-[#AEADA8]">(bobot = task + subtask)</span>
              </h3>
              <div className="space-y-2.5">
                {ranked.map((m) => (
                  <div key={m.name} className="flex items-center gap-3">
                    <Avatar name={m.name} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[13px] font-body font-semibold text-[#1A1916] truncate">{m.name}</span>
                        <span className="flex items-center gap-2 flex-shrink-0">
                          {m.weight === 0 && (
                            <span className="text-[9px] uppercase tracking-wider font-body font-bold text-[#94A3B8] bg-[#F1F5F9] border border-[#E2E8F0] px-1.5 py-0.5 rounded">Kosong</span>
                          )}
                          {m.stats.overdue > 0 && (
                            <span className="flex items-center gap-0.5 text-[10px] font-body font-semibold text-[#DC2626]"><AlertTriangle className="w-3 h-3" />{m.stats.overdue}</span>
                          )}
                          <span className="text-[12px] font-display font-bold text-[#1A1916]">bobot {m.weight}</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-[#F0EFE9] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(m.weight / maxWeight) * 100}%`, backgroundColor: picColor(m.name) }} />
                      </div>
                      <p className="text-[10px] text-[#AEADA8] font-body mt-1">
                        {m.stats.total} task · {m.subtaskTotal} subtask · <span className="text-[#B45309]">{m.stats.active} aktif</span> · {m.stats.todo} todo · <span className="text-[#15803D]">{m.stats.done} done</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {narrative ? (
              <div className="py-2 border-t border-[#F0EFE9]"><Markdown text={narrative} /></div>
            ) : (
              <div className="py-3 border-t border-[#F0EFE9] no-print">
                <p className="text-[12px] text-[#AEADA8] font-body italic">
                  Catatan laporan belum diisi.
                </p>
              </div>
            )}

            {/* Rincian per anggota */}
            <div className="pt-3 border-t border-[#F0EFE9]">
              <h3 className="font-display text-[15px] font-bold text-[#1A1916] mb-3">Rincian per Anggota</h3>
              <div className="space-y-5">
                {ranked.map((m) => (
                  <div key={m.name}>
                    <div className="flex items-center gap-2 mb-2">
                      <Avatar name={m.name} />
                      <p className="text-[13px] font-body font-semibold text-[#1A1916]">{m.name}</p>
                      <span className="text-[11px] text-[#C0BDB6] font-body">bobot {m.weight}</span>
                    </div>
                    <div className="pl-4">
                      <TaskBreakdown grouped={m.grouped} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
